import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    fetchLikesSentDonneurOrdre,
    fetchLikesReceivedDonneurOrdre,
    sendLikeDonneurOrdre,
} from "../../redux/actions/likeActions";

import AppelOffreCard from "../AppelOffreCard";
import PrestataireCardLike from "../PrestataireCardLike";
import ModalInfo from "../ModalInfo";
import { setActiveSection } from "../../redux/actions/dashboardActions";
import { fetchMatchesDonneurOrdre } from "../../redux/actions/matchActions";

/**
 * PROPS :
 * type      : "sent" | "received"
 * idProjet  : filtrage des likes (uniquement pour donneur d'ordre)
 */
const LikesDonneurOrdreDashboard = ({ type = "sent", idProjet = null }) => {
    const dispatch = useDispatch();
    const { token, user } = useSelector((state) => state.auth);
    const { sentDonneurOrdre, receivedDonneurOrdre } = useSelector((state) => state.like);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [showModalInfo, setShowModalInfo] = useState(false);
    const [error, setError] = useState("");

    const role = user?.rolesUser?.[0]?.roleUser; // prestataire / donneur_ordre

    /** ---- LOAD ---- */
    useEffect(() => {
        if (!token) return;
        dispatch(fetchLikesSentDonneurOrdre(token));
        dispatch(fetchLikesReceivedDonneurOrdre(token));
    }, [token, dispatch]);

    /** ---- RAW LIST ---- */
    const rawList = type === "sent" ? sentDonneurOrdre : receivedDonneurOrdre;

    /** ---------------------------
     *  FILTRAGE POUR DONNEUR D’ORDRE
     *  --------------------------- */
    const list = useMemo(() => {
        // Prestataire → pas de filtrage
        if (role !== "donneur_ordre") return rawList;

        // Si aucun idProjet n'est passé, on montre tout
        if (!idProjet) return rawList;

        // Filtrer par idProjet
        return rawList.filter(
            like => like?.projetDonneurOrdre?.idProjet === idProjet
        );
    }, [rawList, role, idProjet]);

    /** ---- RESET INDEX ---- */
    useEffect(() => {
        if (currentIndex >= list.length) {
            setCurrentIndex(0);
        }
    }, [list, currentIndex]);

    const like = list[currentIndex] || {};

    const project = like?.projetDonneurOrdre;
    const prestataire = like?.prestataire?.profilPrestataire;

    const handleLike = async () => {
        if (!token || !like) return;

        if (role === "prestataire") {
            try {
                await dispatch(sendLikeDonneurOrdre(project.idProjet, null, token));
            } catch (error) {
                console.error("Erreur lors du like :", error.message);

                if (error.message.includes("Crédits likes insuffisants")) {
                    setShowModalInfo(true);
                } else {
                    setError(error.message || "Une erreur est survenue");
                }
            }
        } else {
            // DONNEUR ORDRE
            dispatch(
                sendLikeDonneurOrdre(
                    project.idProjet,
                    prestataire.idPrestataire,
                    token
                )
            );
        }

        dispatch(fetchLikesReceivedDonneurOrdre(token));
        dispatch(fetchLikesSentDonneurOrdre(token));
        dispatch(fetchMatchesDonneurOrdre(token));
    };

    const handlePass = () => {
        if (currentIndex < list.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setCurrentIndex(0);
        }
    };

    /** ---- MODAL ---- */
    if (showModalInfo) {
        return (
            <ModalInfo
                message="Vous devez acheter un crédit like pour pouvoir liker un appel d'offre"
                txtBtnCancel={"OK"}
                txtBtnValid={"VOIR LES OFFRES"}
                onValid={() => {
                    dispatch(setActiveSection("offres"));
                    setShowModalInfo(false);
                }}
                onCancel={() => setShowModalInfo(false)}
            />
        );
    }

    /** ---- RENDER ---- */
    return (
        <div style={{ textAlign: "center", margin: 20 }}>
            <h2>{type === "sent" ? "Likes envoyés" : "Likes reçus"}</h2>

            {list.length === 0 ? (
                <p>Aucune demande pour ce projet</p>
            ) : (
                <>
                    {role === "prestataire" ? (
                        <AppelOffreCard
                            project={project}
                            onLike={type === "received" ? handleLike : null}
                            onPass={handlePass}
                            isReceivedLike={type === "received"}
                            isAlreadyMatched={false}
                            isLikeSent={type === "sent"}
                        />
                    ) : (
                        <PrestataireCardLike
                            prestataire={prestataire}
                            projet={project}
                            apiUrl={process.env.REACT_APP_API_URL}
                            onLike={type === "received" ? handleLike : null}
                            onPass={handlePass}
                            isReceivedLike={type === "received"}
                            isAlreadyMatched={false}
                            isLikeSent={type === "sent"}
                        />
                    )}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            maxWidth: "500px",
                            marginTop: 15,
                        }}
                    >
                        <button
                            disabled={currentIndex === 0}
                            onClick={() => setCurrentIndex(prev => prev - 1)}
                        >
                            ◀
                        </button>

                        <span>
                            {currentIndex + 1} / {list.length}
                        </span>

                        <button
                            disabled={currentIndex === list.length - 1}
                            onClick={() => setCurrentIndex(prev => prev + 1)}
                        >
                            ▶
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default LikesDonneurOrdreDashboard;
